'use client'

import { useContext, useState, ReactNode } from 'react'
import { Cell, Spacer, Text, Icon, Card } from '@vinted/web-ui'
import { ElectronicsBadgeMultichrome32 } from '@vinted/multichrome-icons'
import { useIntl } from 'react-intl'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'
import {
  EscrowFeesContext,
  ShippingDetailsContext,
} from '@marketplace-web/item-page/item-page-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { clickEvent, itemPageViewEvent } from '@marketplace-web/escrow-pricing/escrow-pricing-data'

import { ItemPageElectronicsVerificationBlockPluginModel } from './types'

import ElectronicsVerificationModal from '../ElectronicsVerificationModal'

type Props = {
  data: ItemPageElectronicsVerificationBlockPluginModel | undefined
}

const ItemPageElectronicsVerificationBlockPlugin = ({ data }: Props) => {
  const { user } = useSession()
  const { locale } = useIntl()
  const { shippingDetails } = useContext(ShippingDetailsContext)
  const { escrowFees } = useContext(EscrowFeesContext)

  const { track } = useTracking()

  const isViewingOwnItem = user?.id === data?.itemUserId
  const userSide = isViewingOwnItem ? 'seller' : 'buyer'

  const translate = useTranslate(`item.electronics_verification_service.${userSide}`)

  const [isModalOpen, setIsModalOpen] = useState(false)

  if (!shippingDetails) return null
  if (!data) return null

  const getFormattedVerificationFee = () => {
    const finalPrice = escrowFees?.electronicsVerification?.finalPrice

    return finalPrice && formatCurrencyAmount(finalPrice, locale)
  }

  const formattedVerificationFee = getFormattedVerificationFee()

  if (!formattedVerificationFee) return null

  const handleLearnMoreClick = () => {
    setIsModalOpen(true)

    const target = isViewingOwnItem
      ? 'electronics_verification_seller'
      : 'electronics_verification_buyer'

    track(clickEvent({ screen: 'item', target }))
  }

  const howItWorksLink = {
    'how-it-works': (chunks: Array<ReactNode>) => (
      <span>
        <button type="button" onClick={handleLearnMoreClick}>
          <Text
            as="span"
            clickable
            text={chunks}
            type={Text.Type.Subtitle}
            testId="electronics-verification-block-how-it-works-link"
          />
        </button>
      </span>
    ),
  }

  const renderContent = () => (
    <>
      <Spacer />
      <Cell
        styling={Cell.Styling.Tight}
        prefix={
          <Icon
            testId="electronics-verification-block-icon"
            name={ElectronicsBadgeMultichrome32}
            aria={{
              'aria-hidden': 'true',
            }}
          />
        }
        title={
          <Text
            as="h2"
            text={translate('title')}
            theme="amplified"
            type={Text.Type.Title}
            testId="electronics-verification-block-title"
          />
        }
        body={
          <>
            {!isViewingOwnItem && (
              <>
                <Text
                  as="h3"
                  text={translate('optional_price_text', { amount: formattedVerificationFee })}
                  theme="inherit"
                  type={Text.Type.Subtitle}
                  testId="electronics-verification-block-amount"
                />
                <Spacer />
              </>
            )}
            <Text
              as="h3"
              text={translate('body', howItWorksLink)}
              theme="inherit"
              type={Text.Type.Subtitle}
              testId="electronics-verification-block-description"
              html
            />
          </>
        }
      />
      <ElectronicsVerificationModal
        screen="item"
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isViewingOwnItem={isViewingOwnItem}
        verificationFee={escrowFees?.electronicsVerification?.finalPrice}
        viewEvent={itemPageViewEvent({
          target: 'electronics_verification_buyer',
          item_id: data.itemId.toString(),
        })}
      />
    </>
  )

  return (
    <Card>
      <div className="u-overflow-hidden">
        <Cell>{renderContent()}</Cell>
      </div>
    </Card>
  )
}

export default ItemPageElectronicsVerificationBlockPlugin
